import { useMutation } from '@tanstack/react-query';
import { IEmbedV2Response } from '@opengov/reporting-types';
import { save as saveEmbedSDK } from '@opengov/reporting-embeds';

import { useToast } from '../../../../stories/components/Toast/context/useToast.context';
import { IEmbedCreateEmbedPayload } from '../types';
import { embedPickerQueryKeys } from './embedPickerQueryKeys';

// Variables passed to mutate() — payload fields plus an optional AbortController
// (kept local in the component and passed into the mutation fn for cancellation).
export type CreateEmbedVariables = Omit<IEmbedCreateEmbedPayload, 'controller'> & {
  controller?: AbortController;
};

export function useCreateEmbed() {
  const { toggleToast } = useToast();

  return useMutation<IEmbedV2Response, Error, CreateEmbedVariables>({
    mutationKey: [...embedPickerQueryKeys.all, 'createEmbed'],
    mutationFn: async ({
      controller,
      title,
      description,
      reportId,
      savedViewId,
      shouldSync,
      type = 'default',
      groupDataBy,
      tableColumns,
      reportVersion,
      displayAccountCodes,
      id
    }) => {
      // The v2 embed SDK expects a snake_case payload.
      type SnakeCasePayload = {
        id?: string;
        title: string;
        description: string;
        report_id: string;
        saved_view_id: string;
        should_sync: boolean;
        report_version?: string;
        type?: string;
        display_account_codes?: boolean;
        group_data_by?: string;
        table_columns?: string[];
      };

      let payload: SnakeCasePayload = {
        id,
        title,
        description,
        report_id: reportId,
        saved_view_id: savedViewId,
        should_sync: shouldSync,
        report_version: reportVersion,
        type,
        display_account_codes: displayAccountCodes
      };

      // groupDataBy & tableColumns are used only for transparency_table tiles
      if (
        type === 'transparency_table' &&
        groupDataBy &&
        tableColumns &&
        tableColumns.length > 0 &&
        tableColumns.length < 3
      ) {
        payload = { ...payload, group_data_by: groupDataBy, table_columns: tableColumns };
      }

      const result = await saveEmbedSDK(payload, controller);
      if (!result?.id) {
        throw new Error('Could not create embed');
      }
      return result as IEmbedV2Response;
    },
    onError: (error) => {
      if (error?.name !== 'AbortError') {
        toggleToast({ title: 'Could not create embed', severity: 'error' });
      }
    }
  });
}
